import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { queryClient } from 'providers/AllProviders';
import { createVoice, getBusinessLanguageAccents, getBusinessVoice, getBusinessVoices } from './api';
import { VoiceResponse } from './types';
import { useParams } from 'react-router-dom';

export function useGetBusinessVoices(
  params?: { voice_type: string },
  options?: UseQueryOptions<{ data: VoiceResponse }, Error>
) {
  const { locationId, agentId } = useParams<{
    locationId: string;
    agentId: string;
  }>();
  return useQuery<{ data: VoiceResponse }>({
    queryKey: ['voices', locationId, agentId, params],
    queryFn: () => getBusinessVoices(locationId, agentId, params),
    ...options
  });
}

export function useGetBusinessVoice(id?: string, options?: UseQueryOptions<{ data: VoiceResponse }, Error>) {
  const { locationId, agentId } = useParams<{
    locationId: string;
    agentId: string;
  }>();
  return useQuery<{ data: VoiceResponse }>({
    queryKey: ['voice', locationId, agentId, id],
    queryFn: () => getBusinessVoice(id, locationId, agentId),
    enabled: Boolean(id),
    ...options
  });
}

export function useGetBusinessLanguageAccents(code?: string, options?: UseQueryOptions<{ data: string[] }, Error>) {
  const { locationId, agentId } = useParams<{
    locationId: string;
    agentId: string;
  }>();
  return useQuery<{ data: string[] }>({
    queryKey: ['language_accents', locationId, agentId, code],
    queryFn: () => getBusinessLanguageAccents(code, locationId, agentId),
    enabled: Boolean(code),
    ...options
  });
}

export function useCreateVoice() {
  const { locationId, agentId } = useParams<{
    locationId: string;
    agentId: string;
  }>();
  return useMutation({
    mutationFn: (payload: FormData) => createVoice(locationId, agentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['voices'],
        refetchType: 'active'
      });
    }
  });
}
